import * as React from 'react';
import { DayOfWeek, LocalDate, TemporalAdjusters } from 'js-joda';
import { chunk, includes, isEmpty, map } from 'es-toolkit/compat';
import clsx from 'clsx';

import { DisplayMonth, DisplayWeekday } from '@tock/time';
import { IconChevronSmallLeft, IconChevronSmallRight } from '@tock/icons';
import { Typography } from 'omakase';
import {
  accessKeyAwareOnClickWrapper,
  markStandardKeyboardClickIntent,
} from 'utils/accessKeyUtils';
import { ifClickable } from '@tock/shared/utils/touchUtils';
import { range } from 'utils/calendar';
import BaseComponent from '@tock/shared/components/BaseComponent';
import ConsumerCalendarDay, {
  Props as ConsumerCalendarDayProps,
} from 'components/calendar/ConsumerCalendarDay';

const weekdaysArray = [0, 1, 2, 3, 4, 5, 6];

interface Props {
  availableDates?: DateAvailabilityObject;
  availabilityOverride?: boolean;
  disableNext?: boolean;
  disablePrev?: boolean;
  endDate?: LocalDate;
  focusDate?: LocalDate;
  handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, day: string): void;
  lockedDates?: DateAvailabilityObject;
  month: LocalDate;
  isMonthActiveForDayTabbing: boolean;
  monthId: 'calendar-first' | 'calendar-next';
  offeredDates?: DateAvailabilityObject;
  openDates: DateAvailabilityObject;
  selectedDates: string[];
  soldDates?: DateAvailabilityObject;
  onClickNext(): void;
  onClickPrev(): void;
  onActivateDate(date?: string, monthId?: 'calendar-first' | 'calendar-next'): void;
  onTabAwayFromDays(): void;
  onSelectDate(date: string): void;
  ignoreYear?: boolean;
  'data-testid'?: string;
  includeAccessKey: boolean;
}

const RELEVANT_ACCESS_KEY = 'c';

interface DateAvailabilityObject {
  [date: string]: boolean;
}

/**
 * This renders a ConsumerCalendar month that implements sensible focus behaviors,
 * which allows the user to move a selection rectangle using arrow keys, but does
 * *not* make every day in the month tabbable. If props.isMonthActiveForDayTabbing is true,
 * then the month will be rendered with <= 1 tabbable date, which maps to props.focusDate.
 */
class ConsumerCalendarMonthSmartTabbing extends BaseComponent<Props> {
  handleSelectDate(date: string) {
    this.props.onSelectDate(date);
  }

  handleClickNext(e: React.SyntheticEvent) {
    e.preventDefault();
    this.props.onClickNext();
  }

  handleClickPrev(e: React.SyntheticEvent) {
    e.preventDefault();
    this.props.onClickPrev();
  }

  render() {
    const {
      availabilityOverride,
      availableDates,
      handleKeyDown,
      ignoreYear,
      lockedDates,
      month,
      monthId,
      offeredDates,
      onActivateDate,
      onTabAwayFromDays,
      openDates,
      selectedDates,
      soldDates,
      includeAccessKey,
    } = this.props;
    const today = LocalDate.now();
    const selectedDateInThisMonth = selectedDates
      .map((selectedDate) => LocalDate.parse(selectedDate))
      .find(
        (selectedDate) =>
          selectedDate.year() === month.year() && selectedDate.monthValue() === month.monthValue()
      );
    const monthLabel = new Date(month.year(), month.monthValue() - 1, 1).toLocaleDateString(
      undefined,
      { month: 'long', year: 'numeric' }
    );
    const selectedDateLabel = selectedDateInThisMonth
      ? new Date(
          selectedDateInThisMonth.year(),
          selectedDateInThisMonth.monthValue() - 1,
          selectedDateInThisMonth.dayOfMonth()
        ).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
      : undefined;
    const monthHeadingAriaLabel = selectedDateLabel
      ? `${monthLabel}. Selected date ${selectedDateLabel}. Press to make a new date selection in this month.`
      : `${monthLabel}. Press to make a date selection in this month.`;
    const monthHeadingId = `consumer-calendar-month-${monthId}`;
    const { max } = range(openDates);
    const definedDate = this.props.endDate ? this.props.endDate : LocalDate.now();
    const maxDate = max ? LocalDate.parse(max) : definedDate;
    const days: ConsumerCalendarDayProps[] = [];
    let firstEnabledDateInMonth: string | undefined;
    const startOfMonth = month.with(TemporalAdjusters.firstDayOfMonth());
    let cursor = startOfMonth;
    if (cursor.dayOfWeek() !== DayOfWeek.SUNDAY) {
      cursor = cursor.minusDays(cursor.dayOfWeek().value());
    }
    let endOfMonth = month.with(TemporalAdjusters.lastDayOfMonth());
    // If the month ends on a Sunday, then the end of that isoWeek - 1 is the saturday before the end of the month.
    // In that edge case, we need to render an extra week to get to the next Saturday, so add seven days
    if (endOfMonth.dayOfWeek() === DayOfWeek.SUNDAY) {
      endOfMonth = endOfMonth.plusDays(7);
    }
    const endDate = endOfMonth.plusDays(6 - endOfMonth.dayOfWeek().value());
    let focusDate = this.props.focusDate;
    while (!cursor.isAfter(endDate)) {
      const isPast = cursor.isBefore(today);
      const isFuture = cursor.isAfter(maxDate);
      const isToday = cursor.isEqual(today);
      const curDate = cursor.toString();
      const isClosed = isEmpty(openDates)
        ? isPast || isFuture
        : !isPast && !isFuture && !openDates[curDate];
      const isAvailable = availabilityOverride
        ? !isPast && !isClosed
        : availableDates && !!availableDates[curDate];
      const isLocked = lockedDates ? !!lockedDates[curDate] && !isAvailable : false;
      const isSoldOut = soldDates ? !!soldDates[curDate] && !isAvailable && !isLocked : false;
      const isOffered = offeredDates ? !!offeredDates[curDate] : false;
      const isSelected = includes(selectedDates, curDate);
      const isEnabled =
        !isFuture && (isAvailable || isLocked || isSoldOut || isOffered) && !isClosed;
      const isNotAvailable =
        !isPast && !isFuture && !isAvailable && !isSoldOut && !isLocked && !isClosed;
      let focused = false;
      if (this.props.isMonthActiveForDayTabbing && focusDate && focusDate.isEqual(cursor)) {
        if (isEnabled) {
          focused = true;
        } else {
          focusDate = focusDate.plusDays(1);
        }
      }

      const day: ConsumerCalendarDayProps = {
        text: `${cursor.dayOfMonth()}`,
        date: curDate,
        focused,
        handleKeyDown,
        isOutOfMonth: cursor.month() !== month.month(),
        isPast,
        isFuture,
        isToday,
        isAvailable,
        isSelected,
        isEnabled,
        isTabbable: this.props.isMonthActiveForDayTabbing && focused,
        isSoldOut,
        isClosed,
        isLocked,
        isNotAvailable,
        onClick: this.handleSelectDate,
        onTabAway: onTabAwayFromDays,
      };

      days.push(day);
      if (!firstEnabledDateInMonth && !day.isOutOfMonth && day.isEnabled) {
        firstEnabledDateInMonth = day.date;
      }
      cursor = cursor.plusDays(1);
    }

    return (
      <div className="ConsumerCalendar-month" data-testid={this.props['data-testid']}>
        <div className="ConsumerCalendar-header">
          <button
            aria-label="Go to previous month"
            className={clsx('ConsumerCalendar-headerNav', 'ConsumerCalendar-headerNavPrev', {
              'is-disabled': this.props.disablePrev,
            })}
            onClick={
              !this.props.disablePrev
                ? ifClickable(this.handleClickPrev, 'calendar prev')
                : undefined
            }
            disabled={this.props.disablePrev}
            tabIndex={this.props.disablePrev ? undefined : 0}
            data-testid={'calendar-prev-button_' + this.props['data-testid']}
          >
            <IconChevronSmallLeft />
          </button>

          <div
            className="ConsumerCalendar-monthHeading"
            id={monthHeadingId}
            data-testid={'month-heading_' + this.props['data-testid']}
          >
            <button
              type="button"
              className="ConsumerCalendar-monthHeadingButton"
              aria-label={monthHeadingAriaLabel}
              tabIndex={0}
              onClick={(event) => {
                accessKeyAwareOnClickWrapper({
                  event,
                  onStandardClick: () => {
                    onActivateDate(
                      selectedDateInThisMonth?.toString() || firstEnabledDateInMonth,
                      monthId
                    );
                  },
                });
              }}
              onKeyDown={markStandardKeyboardClickIntent}
              accessKey={includeAccessKey ? RELEVANT_ACCESS_KEY : undefined}
            >
              <Typography color="inherit" component="span" variant="body02Strong">
                <DisplayMonth displayMonthAs="long" monthValue={month} showYear={!ignoreYear} />
              </Typography>
            </button>
          </div>

          <button
            aria-label="Go to next month"
            className={clsx('ConsumerCalendar-headerNav', 'ConsumerCalendar-headerNavNext', {
              'is-disabled': this.props.disableNext,
            })}
            onClick={
              !this.props.disableNext
                ? ifClickable(this.handleClickNext, 'calendar next')
                : undefined
            }
            disabled={this.props.disableNext}
            tabIndex={this.props.disableNext ? undefined : 0}
            data-testid={'calendar-next-button_' + this.props['data-testid']}
          >
            <IconChevronSmallRight />
          </button>
        </div>

        <div aria-labelledby={monthHeadingId} role="grid">
          <div className="ConsumerCalendar-weekdays" role="row">
            {map(weekdaysArray, (weekday) => (
              <div className="ConsumerCalendar-weekday" key={weekday} role="columnheader">
                <Typography color="inherit" component="span" variant="body02">
                  <DisplayWeekday displayWeekdayAs="short" weekdayValue={weekday} />
                </Typography>
              </div>
            ))}
          </div>
          {map(chunk(days, 7), (weekdays, idx) => (
            <div key={idx} className="ConsumerCalendar-week" role="row">
              {map(weekdays, (day) => (
                <ConsumerCalendarDay {...day} key={day.date} />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }
}

export default ConsumerCalendarMonthSmartTabbing;
